import { z } from 'zod';

/**
 * Connectors (PLATFORM_EXPANSION.md §2.4; EXPANSION_DECISIONS.md D0). A
 * connector is a server-side, scheduled fetcher that writes `data_points`. Every
 * connector is provider-agnostic with two paths:
 *
 *   Path 1 — BYO (default): the tenant supplies their own provider + key via the
 *            Integrations settings screen (X3.6). Zero lock-in, zero platform cost.
 *   Path 2 — platform-brokered paid API: the tenant opts in explicitly, having
 *            seen price, terms, licence class, attribution and retention up front.
 *
 * The registry entry below is the contract both paths share. Secrets (BYO keys,
 * brokered credentials) never live here — they are server-only (see X0.5).
 */

/** Licence classes (D0). A connector is only offered where its class permits use. */
export const LICENCE_CLASSES = ['commercial_ok', 'byo_key_only', 'platform_brokered'] as const;
export const LicenceClassSchema = z.enum(LICENCE_CLASSES);
export type LicenceClass = z.infer<typeof LicenceClassSchema>;

/** Data types a connector can produce. Widgets consume normalised values only. */
export const CONNECTOR_DATA_TYPES = [
  'weather',
  'news',
  'sport',
  'solar_forecast',
  'calendar',
  'departures',
  'energy_price',
  'traffic',
  'air_quality',
  'tide',
  'parcel',
  'queue'
] as const;
export const ConnectorDataTypeSchema = z.enum(CONNECTOR_DATA_TYPES);
export type ConnectorDataType = z.infer<typeof ConnectorDataTypeSchema>;

/**
 * Attribution block. Rendered wherever the licence class requires it; the text
 * lives in the registry, never in widget code (D0).
 */
export const AttributionSchema = z.object({
  required: z.boolean(),
  text: z.string().min(1),
  url: z.string().url().optional()
});
export type Attribution = z.infer<typeof AttributionSchema>;

/**
 * One registry entry per provider (D0). Enforced by code for both paths:
 *  - `domains` is the `safeFetch` allowlist — the only hosts this connector may
 *    contact. `safeFetch` is the only outbound path; no scraping, ever.
 *  - `byo_supported` / `brokered` are the dual-path flags.
 *  - `kill switch`, `refresh_interval_s` and `rate_budget_per_hour` bound impact.
 */
export const ConnectorRegistryEntrySchema = z.object({
  id: z.string().min(1).max(64),
  data_type: ConnectorDataTypeSchema,
  /** Human name shown in the Integrations screen. */
  provider_name: z.string().min(1).max(120),
  licence_class: LicenceClassSchema,
  attribution: AttributionSchema,
  /**
   * `safeFetch` host allowlist (no scheme, no path). Non-empty for provider
   * APIs. **Empty** only when `tenant_feed` is true: a tenant-supplied feed URL
   * is an arbitrary host (chosen per tenant in params), so no static allowlist
   * can name it — the SSRF guard in `safeFetch` (https-only, private-address
   * block, redirect re-validation) is the control there.
   */
  domains: z.array(z.string().min(1).max(253)),
  /**
   * The endpoint is a tenant-supplied feed URL (RSS/ICS, X3.4/X3.5) rather than
   * a fixed provider API. Implies `byo_supported: false` (nothing to key) and a
   * feed-terms record required at config save.
   */
  tenant_feed: z.boolean().optional(),
  /** True when a tenant may bring their own key (Path 1). */
  byo_supported: z.boolean(),
  /** True when the platform can broker a paid plan (Path 2). */
  brokered: z.boolean(),
  /** Minimum seconds between refreshes for a single coalesce key. */
  refresh_interval_s: z.number().int().min(30).max(86400),
  /** Per-tenant outbound budget; enforced server-side. */
  rate_budget_per_hour: z.number().int().min(1).max(100000),
  /** Operator kill switch — false stops all fetches for this provider. */
  enabled: z.boolean().default(true),
  /** Path-2 metadata; present only when `brokered` is true. */
  brokered_plan: z.object({
    price_label: z.string().min(1),
    terms_url: z.string().url(),
    retention_days: z.number().int().min(0),
    metered_per: z.literal('screen')
  }).optional()
}).superRefine((entry, ctx) => {
  if (entry.brokered && !entry.brokered_plan) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'brokered connector must declare its brokered_plan terms', path: ['brokered_plan'] });
  }
  if (!entry.brokered && entry.brokered_plan) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'brokered_plan present on a non-brokered connector', path: ['brokered_plan'] });
  }
  if (entry.licence_class === 'platform_brokered' && !entry.brokered) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'platform_brokered licence requires brokered = true', path: ['brokered'] });
  }
  // An empty allowlist means "any host" — only legitimate for a tenant-supplied
  // feed, where the SSRF guard is the control. A fixed provider API must name its hosts.
  if (entry.domains.length === 0 && !entry.tenant_feed) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'an empty domains allowlist requires tenant_feed = true', path: ['domains'] });
  }
  if (entry.tenant_feed && entry.byo_supported) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'a tenant-supplied feed has nothing to key (byo_supported must be false)', path: ['byo_supported'] });
  }
});
export type ConnectorRegistryEntry = z.infer<typeof ConnectorRegistryEntrySchema>;

/**
 * Per-tenant connector configuration. `params` is provider-shaped but contains
 * NO secrets (location, feed URLs, tilt/azimuth/kWp — everything that exports
 * cleanly per D0's portability guarantee). Secrets are separate and server-only.
 */
export const ConnectorConfigSchema = z.object({
  tenant: z.string().min(1),
  connector_id: z.string().min(1),
  /** Which path this tenant chose. */
  path: z.enum(['byo', 'brokered']),
  params: z.record(z.unknown()).default({}),
  /** False until the tenant finishes config; the widget shows not-configured. */
  configured: z.boolean().default(false),
  enabled: z.boolean().default(true)
});
export type ConnectorConfig = z.infer<typeof ConnectorConfigSchema>;

/** The coalesce key: identical provider + params are fetched once, shared. */
export const CoalesceKeySchema = z.object({
  connector_id: z.string().min(1),
  /** Stable, sorted serialisation of the provider-relevant params. */
  params_hash: z.string().min(1)
});
export type CoalesceKey = z.infer<typeof CoalesceKeySchema>;

/** Freshness rule (X0.5): stale data is marked stale, never shown as fresh. */
export const FetchStateSchema = z.enum(['fresh', 'stale', 'error']);
export type FetchState = z.infer<typeof FetchStateSchema>;
